import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema, paginationQuerySchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";
import { deviceChannelSchema } from "./channels";
import { hardwareIdSchema } from "./hardware";
import {
  appKeySchema,
  frequencyPlanSchema,
  joinEuiSchema,
  networkSyncStateSchema,
} from "./lorawan";

export const DEVICE_STATUSES = ["PENDING", "ACTIVE", "INACTIVE", "MAINTENANCE"] as const;
export const deviceStatusSchema = z.enum(DEVICE_STATUSES);
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

/**
 * What a device measures, which decides the readings to expect from it.
 *
 * Bounded by what telemetry can store — temperature, humidity, battery,
 * distance, occupancy, leak, door, current — or, for IOT_CONTROLLER, by
 * channel rows (ADR-081). A device reporting something else needs a schema
 * change, not just a new member here (ADR-045).
 */
export const DEVICE_TYPES = [
  "TEMPERATURE_HUMIDITY",
  "TEMPERATURE",
  "HUMIDITY",
  "DISTANCE_LEVEL",
  /** Occupied or not. Judged against asset hours (ADR-069). */
  "PRESENCE",
  /** Conducting liquid at a spot probe. Always a fault when true (ADR-077). */
  "WATER_LEAK",
  /** Door/window magnetic contact. Open for the rule's duration is a fault (ADR-078). */
  "MAGNETIC_CONTACT",
  /** Multi-I/O controller; readings live on DeviceChannel (ADR-081). */
  "IOT_CONTROLLER",
  /** RMS current on one conductor, in amperes (ADR-102). */
  "CURRENT",
] as const;
export const deviceTypeSchema = z.enum(DEVICE_TYPES);
export type DeviceType = z.infer<typeof deviceTypeSchema>;

export const TELEMETRY_PROVIDERS = ["THE_THINGS_STACK", "CHIRPSTACK", "SIMULATOR"] as const;
export const telemetryProviderSchema = z.enum(TELEMETRY_PROVIDERS);
export type TelemetryProvider = z.infer<typeof telemetryProviderSchema>;

/**
 * LoRaWAN DevEUI: 16 hex characters, stored uppercase.
 *
 * Accepted with the separators people copy out of a console or read off a
 * label, then normalized, because rejecting "24e1-2472-5e10-0001" would be
 * pedantry aimed at whoever is provisioning a hundred sensors.
 */
export const devEuiSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s:-]/g, "").toUpperCase())
  .refine((value) => /^[0-9A-F]{16}$/.test(value), {
    message: "DevEUI must be 16 hexadecimal characters.",
  });

/**
 * How long without an uplink before a device counts as offline in listings.
 *
 * A flat default: per-device thresholds already exist in DeviceConfiguration
 * and the alerting engine will use them in phase 6. This constant only drives
 * the badge in a list, where being a few minutes off changes nothing.
 *
 * Three reporting intervals of five minutes (ADR-094), so one lost uplink is
 * not enough to flip a healthy sensor.
 */
export const DEFAULT_OFFLINE_AFTER_MINUTES = 15;

export const CONNECTIVITY_STATES = ["ONLINE", "OFFLINE", "NEVER_SEEN"] as const;
export const connectivitySchema = z.enum(CONNECTIVITY_STATES);
export type Connectivity = z.infer<typeof connectivitySchema>;

/**
 * Last reading against the target range of the asset the device is watching.
 *
 * Named for the question rather than for one metric: whichever quantity the
 * device measures is compared against the matching range on its asset, so a
 * radar level sensor is judged on distance and a thermometer on temperature.
 * It was `temperatureState` until distance sensing arrived and the name
 * started describing only half the hardware (ADR-060).
 */
export const RANGE_STATES = ["IN_RANGE", "OUT_OF_RANGE", "UNKNOWN"] as const;
export const rangeStateSchema = z.enum(RANGE_STATES);
export type RangeState = z.infer<typeof rangeStateSchema>;

export const deviceSchema = z.object({
  id: z.uuid(),
  devEui: z.string(),
  type: deviceTypeSchema,
  model: z.string(),
  manufacturer: z.string(),
  supplier: z.string().nullable(),

  organizationId: z.uuid(),
  siteId: z.uuid().nullable(),
  siteName: z.string().nullable(),
  assetId: z.uuid().nullable(),
  assetName: z.string().nullable(),

  provider: telemetryProviderSchema,
  loraApplicationId: z.string().nullable(),
  loraDeviceId: z.string().nullable(),
  joinEui: z.string().nullable(),
  /** Null for a simulated device, which never reaches a Network Server. */
  frequencyPlan: frequencyPlanSchema.nullable(),
  networkSyncState: networkSyncStateSchema,
  /** Sanitized reason for FAILED or DRIFT. */
  networkSyncError: z.string().nullable(),
  networkSyncedAt: z.iso.datetime().nullable(),

  status: deviceStatusSchema,
  connectivity: connectivitySchema,
  rangeState: rangeStateSchema,

  lastSeenAt: z.iso.datetime().nullable(),
  lastTemperatureC: z.number().nullable(),
  lastHumidityPercent: z.number().nullable(),
  lastBatteryPercent: z.number().nullable(),
  /** Millimetres from the sensor to what it is pointed at (ADR-060). */
  lastDistanceMm: z.number().nullable(),
  /**
   * Whether the newest reading found somebody there (ADR-069).
   *
   * Null covers both "this sensor does not report occupancy" and "it has not
   * reported yet", which is the same distinction every other `last*` field
   * makes.
   */
  lastOccupied: z.boolean().nullable(),
  /** Whether the newest reading found liquid at the probe (ADR-077). */
  lastLeaking: z.boolean().nullable(),
  /** Whether the newest reading found the contact open (ADR-078). */
  lastDoorOpen: z.boolean().nullable(),
  /** Newest RMS current, in amperes (ADR-102). Null means never reported. */
  lastCurrentA: z.number().nullable(),

  /**
   * Controller channels (ADR-081). Empty for mono-metric sensors; present
   * (possibly empty until first materialisation) for IOT_CONTROLLER.
   */
  channels: z.array(deviceChannelSchema),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type Device = z.infer<typeof deviceSchema>;

/**
 * A device is always created PENDING: status is earned by reporting, not
 * declared at registration, so it is not accepted here.
 */
export const createDeviceRequestSchema = z
  .object({
    devEui: devEuiSchema,
    // Stated, not defaulted, so no unlabelled device is recorded as the model
    // the pilot happened to start with (ADR-045) — but chosen from the
    // hardware this system can actually ingest rather than typed free-hand
    // (ADR-050). The model, manufacturer and measured type all follow from it.
    hardwareId: hardwareIdSchema,
    // The reseller genuinely varies for the same hardware, so it stays free
    // text.
    supplier: z.string().trim().max(60).optional(),
    siteId: z.uuid().optional(),
    assetId: z.uuid().optional(),
    provider: telemetryProviderSchema.default("THE_THINGS_STACK"),
    /**
     * Typed by hand only while provisioning is off. With it on, both are
     * derived (`eui-<deveui>` and the configured application) and a value
     * here that disagrees is refused (ADR-089).
     */
    loraApplicationId: z.string().trim().max(120).optional(),
    loraDeviceId: z.string().trim().max(120).optional(),
    /** Defaults to the hardware's vendor JoinEUI where its documentation states one. */
    joinEui: joinEuiSchema.optional(),
    /** Defaults to the deployment's plan; must be one the hardware supports. */
    frequencyPlan: frequencyPlanSchema.optional(),
    /**
     * Entered by the technician and mandatory for anything that is not a
     * simulator. Forwarded to the Join Server and never stored, logged or
     * returned (ADR-089).
     */
    appKey: appKeySchema.optional(),
  })
  .extend(tenantScopeSchema.shape)
  .refine((value) => value.provider === "SIMULATOR" || value.appKey !== undefined, {
    message: "AppKey is required to register a device.",
    path: ["appKey"],
  });

export type CreateDeviceRequest = z.infer<typeof createDeviceRequestSchema>;

/**
 * PENDING is absent on purpose: a device that has reported cannot go back to
 * never having been provisioned.
 */
export const updateDeviceStatusSchema = z.enum(["ACTIVE", "INACTIVE", "MAINTENANCE"]);

export const updateDeviceRequestSchema = z
  .object({
    /** Swapping the hardware out, which also resets model, manufacturer and type. */
    hardwareId: hardwareIdSchema,
    supplier: z.string().trim().max(60).nullable(),
    siteId: z.uuid().nullable(),
    assetId: z.uuid().nullable(),
    loraApplicationId: z.string().trim().max(120).nullable(),
    loraDeviceId: z.string().trim().max(120).nullable(),
    /** Must be one the hardware supports and match every linked gateway. */
    frequencyPlan: frequencyPlanSchema,
    status: updateDeviceStatusSchema,
  })
  .partial()
  // An empty PATCH is a client bug worth surfacing, not a silent no-op.
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  });

export type UpdateDeviceRequest = z.infer<typeof updateDeviceRequestSchema>;

/**
 * A new OTAA root key for a device already registered from here (ADR-089).
 * Written to the Join Server only; nothing is stored and the response does
 * not contain it.
 */
export const replaceDeviceAppKeyRequestSchema = z.object({ appKey: appKeySchema });

export type ReplaceDeviceAppKeyRequest = z.infer<typeof replaceDeviceAppKeyRequestSchema>;

export const listDevicesQuerySchema = listQuerySchema.extend({
  status: deviceStatusSchema.optional(),
  type: deviceTypeSchema.optional(),
  siteId: z.uuid().optional(),
  assetId: z.uuid().optional(),
  connectivity: connectivitySchema.optional(),
});

export type ListDevicesQuery = z.infer<typeof listDevicesQuerySchema>;

export const deviceListSchema = paginatedSchema(deviceSchema);
export type DeviceList = z.infer<typeof deviceListSchema>;

// =============================================================================
// Telemetry read
// =============================================================================

export const telemetryReadingSchema = z.object({
  id: z.uuid(),
  measuredAt: z.iso.datetime(),
  receivedAt: z.iso.datetime(),
  temperatureC: z.number().nullable(),
  humidityPercent: z.number().nullable(),
  batteryPercent: z.number().nullable(),
  /** Millimetres. A distance from the sensor, not a level (ADR-060). */
  distanceMm: z.number().nullable(),
  /** Occupied or not at this moment. Null means the reading did not say. */
  occupied: z.boolean().nullable(),
  /** Liquid at the probe or not. Null means the reading did not say. */
  leaking: z.boolean().nullable(),
  /** Contact open or not. Null means the reading did not say. */
  doorOpen: z.boolean().nullable(),
  /** RMS current in amperes. Null means the reading did not say (ADR-102). */
  currentA: z.number().nullable(),
  rssi: z.number().int().nullable(),
  snr: z.number().nullable(),
});

export type TelemetryReading = z.infer<typeof telemetryReadingSchema>;

/** Hours of history returned when the caller gives no explicit range. */
export const DEFAULT_TELEMETRY_WINDOW_HOURS = 24;

export const listTelemetryQuerySchema = paginationQuerySchema
  .extend({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
  })
  .refine((value) => !value.from || !value.to || new Date(value.from) < new Date(value.to), {
    message: "`from` must be earlier than `to`.",
    path: ["from"],
  });

export type ListTelemetryQuery = z.infer<typeof listTelemetryQuerySchema>;

export const telemetryListSchema = paginatedSchema(telemetryReadingSchema).extend({
  /** Echoes the window actually queried, including the applied default. */
  range: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
});

export type TelemetryList = z.infer<typeof telemetryListSchema>;

// =============================================================================
// Aggregated telemetry series (charts over windows wider than one page)
// =============================================================================

/**
 * Bucket widths for downsampling.
 *
 * Raw pages cap at 100 points; a 7-day window at 15-minute cadence is ~672
 * readings, so the chart asks for hourly (or daily) aggregates instead of
 * silently sampling the newest hundred (ADR-087).
 */
export const TELEMETRY_BUCKETS = ["1h", "1d"] as const;
export const telemetryBucketSchema = z.enum(TELEMETRY_BUCKETS);
export type TelemetryBucket = z.infer<typeof telemetryBucketSchema>;

const metricStatsSchema = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
  avg: z.number().nullable(),
  /** Newest reading that landed in the bucket; null when the bucket is empty of that metric. */
  last: z.number().nullable(),
});

export type MetricStats = z.infer<typeof metricStatsSchema>;

export const telemetrySeriesPointSchema = z.object({
  bucketStart: z.iso.datetime(),
  count: z.number().int().nonnegative(),
  temperatureC: metricStatsSchema,
  humidityPercent: metricStatsSchema,
  batteryPercent: metricStatsSchema,
  distanceMm: metricStatsSchema,
  currentA: metricStatsSchema,
  rssi: metricStatsSchema,
  snr: metricStatsSchema,
  /** Last boolean observed in the bucket; null when none of the rows said. */
  occupied: z.boolean().nullable(),
  leaking: z.boolean().nullable(),
  doorOpen: z.boolean().nullable(),
});

export type TelemetrySeriesPoint = z.infer<typeof telemetrySeriesPointSchema>;

export const listTelemetrySeriesQuerySchema = z
  .object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    bucket: telemetryBucketSchema.default("1h"),
  })
  .refine((value) => !value.from || !value.to || new Date(value.from) < new Date(value.to), {
    message: "`from` must be earlier than `to`.",
    path: ["from"],
  });

export type ListTelemetrySeriesQuery = z.infer<typeof listTelemetrySeriesQuerySchema>;

export const telemetrySeriesSchema = z.object({
  bucket: telemetryBucketSchema,
  range: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  data: z.array(telemetrySeriesPointSchema),
});

export type TelemetrySeries = z.infer<typeof telemetrySeriesSchema>;
